import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { Assets } from "../../assets";
import { ChevronDown } from "../../assets/ChevronDown";
import { ChevronUp } from "../../assets/ChevronUp";
import { Close } from "../../assets/Close";
import { Pill } from "../Pill";
import { Container, LabelContainer, SelectBoxWrapper, SingleValueContent, HelpMessage, ClearableContainer, IconWrapper, TextContainer, MultipleValueContent, SelectDropdown, OpenButtonContainer, ValueWrapper, SelectDropdownPositioner } from "./Select.styles";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 *  A Select component.
 *
 * @param value Value of the select
 * @param label Label of the select
 * @param placeholder Placeholder of the select
 * @param size Size of the select
 * @param leftIcon Icon to be displayed on the left side of the select
 * @param helpMessage Help message to be displayed
 * @param helpGutter If the help message should have a gutter
 * @param clearable If the select should be clearable
 * @param error If the select has an error
 * @param disabled If the select is disabled
 * @param multiple If the select is multiple
 * @param maxBoxRows Maximum number of rows to be displayed in the select box
 * @param maxDropdownRows Maximum number of rows to be displayed in the dropdown
 * @param name Name of the select
 * @param boxScrollDirection Direction of the scroll in the select box
 * @param onChange Function to be called when the select value changes
 * @param onFocus Function to be called when the select is focused
 * @param onBlur Function to be called when the select is blurred
 * @param children Select items
 */
const Select = props => {
  const {
    value,
    label,
    placeholder,
    size = 'md',
    leftIcon,
    helpMessage,
    helpGutter = true,
    clearable,
    error,
    disabled,
    multiple,
    maxBoxRows = 4,
    maxDropdownRows = 4,
    name,
    boxScrollDirection = 'vertical',
    children,
    onChange,
    onFocus,
    onBlur
  } = props;
  const theme = useTheme();
  const [firstUpdate, setFirstUpdate] = useState(true);
  const [selectedItems, setSelectedItems] = useState(Array.isArray(value) ? value ?? '' : [value ?? '']);
  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const containerRef = useRef(null);
  const boxRef = useRef(null);
  const dropdownRef = useRef(null);
  const LeftIcon = leftIcon && Assets[leftIcon];
  const selectItems = Array.isArray(children) ? children : [children];
  useLayoutEffect(() => {
    setFirstUpdate(false);
  }, []);

  /**
   * Updates the selected items when the value prop changes
   * This is needed because the value prop can be updated from outside the component (e.g. when using a form)
   */
  useEffect(() => {
    if (value === selectedItems || value === selectedItems[0]) return;
    if (multiple) setSelectedItems(value ?? []);else setSelectedItems([value ?? '']);
  }, [value]);

  /**
   * Closes the dropdown when clicking outside the select
   */
  useEffect(() => {
    const handleClickOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [containerRef]);

  /**
   * Closes the dropdown when pressing the escape key
   */
  useEffect(() => {
    const handleEscape = event => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
      if (event.key === 'Tab') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape, true);
    return () => document.removeEventListener('keydown', handleEscape, true);
  }, [containerRef]);

  /**
   * Calls the onChange function when the selected items change
   */
  useEffect(() => {
    if (firstUpdate) return;
    if (multiple) {
      onChange?.(selectedItems);
    } else {
      onChange?.(selectedItems[0]);
    }
  }, [selectedItems]);

  /**
   * Calls the onFocus and onBlur functions when the select is opened or closed
   */
  useEffect(() => {
    if (firstUpdate) return;
    if (open) {
      onFocus?.();
    } else {
      onBlur?.();
    }
  }, [open]);

  /**
   * Handles enter, space or arrow down/up key to open the dropdown when the box is focused
   */
  useEffect(() => {
    const handleEnter = event => {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(prevOpen => !prevOpen);
      }
      if (event.key === 'Tab') {
        setOpen(false);
      }
    };
    boxRef.current?.addEventListener('keydown', handleEnter);
    return () => boxRef.current?.removeEventListener('keydown', handleEnter);
  }, [boxRef]);

  /**
   * Sets the focused index to -1 when closing the dropdown
   */
  useEffect(() => {
    if (!open) {
      setFocusedIndex(-1);
    }
  }, [open]);

  /**
   * Handle arrow keys to navigate the dropdown with the focused index and enter/space key to select an item
   */
  useEffect(() => {
    const handleArrowKeys = event => {
      if (!open) return;
      event.stopPropagation();
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setFocusedIndex(prevIndex => (prevIndex + 1) % selectItems.length);
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setFocusedIndex(prevIndex => (prevIndex - 1 + selectItems.length) % selectItems.length);
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const focusedItem = selectItems[focusedIndex];
        if (focusedItem) {
          handleSelectItemByValue(focusedItem.props.value);
        }
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleArrowKeys, true);
    return () => document.removeEventListener('keydown', handleArrowKeys, true);
  }, [open, focusedIndex]);

  /**
   * Scrolls the dropdown to the focused item when the focused index changes
   * This is needed because the dropdown can be scrolled with the arrow keys
   */
  useLayoutEffect(() => {
    if (dropdownRef.current) {
      const focusedItem = dropdownRef.current.children[focusedIndex];
      if (focusedItem) {
        focusedItem?.scrollIntoView?.({
          block: 'nearest'
        });
      }
    }
  }, [focusedIndex]);

  /**
   * Sets the focused index to the selected item when the dropdown is opened
   */
  useLayoutEffect(() => {
    if (open && dropdownRef.current) {
      const selectedIndex = selectItems.findIndex(item => item.props.value === selectedItems[0]);
      setFocusedIndex(selectedIndex);
    }
  }, [selectedItems, open]);

  /**
   * Sets the focused item to a word that starts with the pressed key
   */
  useEffect(() => {
    if (!open) return;
    const handleLetterKeys = event => {
      if (event.key.length === 1) {
        const letter = event.key.toLowerCase();
        const items = selectItems.map(item => item.props.label.toLowerCase());
        const index = items.findIndex(item => item.startsWith(letter));
        if (index !== -1) {
          setFocusedIndex(index);
        }
      }
    };
    document.addEventListener('keydown', handleLetterKeys, true);
    return () => document.removeEventListener('keydown', handleLetterKeys, true);
  }, [open]);

  /**
   * Closes the dropdown when the select is disabled
   * This is needed because the dropdown can be disabled from outside the componen with the dropdown open
   * and the dropdown will stay open
   */
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);
  const getLabelFromChildren = useCallback(value => {
    const item = selectItems.find(item => item.props.value === value);
    return item?.props.label ?? '';
  }, [selectItems]);
  const handleSelectBoxClick = useCallback(() => {
    if (!disabled) {
      setOpen(prevOpen => !prevOpen);
    }
  }, [disabled]);
  const handleSelectItemByValue = useCallback(value => {
    if (multiple) {
      if (selectedItems.includes(value)) {
        setSelectedItems(selectedItems.filter(item => item !== value));
      } else {
        setSelectedItems(prev => [...prev, value]);
      }
    } else {
      setSelectedItems([value]);
      setOpen(false);
    }
  }, [multiple, selectedItems]);
  const boxMaxHeight = useMemo(() => {
    const pillHeight = size === 'sm' ? 28 : size === 'md' ? 28 : 40;
    const pillGap = 5 * (maxBoxRows - 1);
    const boxPadding = size === 'sm' ? 10 : size === 'md' ? 16 : 24;
    const itemsHeight = maxBoxRows * pillHeight + pillGap + boxPadding;
    const minHeight = size === 'sm' ? 40 : size === 'md' ? 48 : 64;
    if (!multiple || minHeight > itemsHeight) return minHeight;
    return itemsHeight;
  }, [multiple, maxBoxRows, size]);
  const dropdownHeight = useMemo(() => {
    const itemHeight = size === 'sm' ? 40 : size === 'md' ? 48 : 64;
    const falseFloorHeight = itemHeight / 2;
    return Math.min(selectItems.length, maxDropdownRows) * itemHeight + falseFloorHeight;
  }, [selectItems, maxDropdownRows, size]);
  return /*#__PURE__*/_jsxs(Container, {
    ref: containerRef,
    size: size,
    disabled: disabled,
    children: [label && /*#__PURE__*/_jsx(LabelContainer, {
      htmlFor: name,
      children: label
    }), /*#__PURE__*/_jsxs(SelectBoxWrapper, {
      ref: boxRef,
      size: size,
      error: error,
      hasLeftIcon: !!leftIcon,
      minHeight: size === 'sm' ? 40 : size === 'md' ? 48 : 64,
      disabled: disabled,
      tabIndex: disabled ? -1 : 0,
      onClick: handleSelectBoxClick,
      children: [LeftIcon && /*#__PURE__*/_jsx(IconWrapper, {
        children: /*#__PURE__*/_jsx(LeftIcon, {
          size: size,
          color: theme.colors.neutralL5
        })
      }), /*#__PURE__*/_jsx(ValueWrapper, {
        children: multiple ? /*#__PURE__*/_jsxs(MultipleValueContent, {
          size: size,
          maxHeight: boxMaxHeight,
          scrollDirection: boxScrollDirection,
          children: [selectedItems.map(item => {
            return /*#__PURE__*/_jsx(Pill, {
              label: getLabelFromChildren(item),
              size: size === 'lg' ? 'sm' : 'xs',
              disabled: disabled,
              onClick: () => {
                setSelectedItems(selectedItems.filter(selectedItem => selectedItem !== item));
              }
            }, item);
          }), selectedItems.length === 0 && /*#__PURE__*/_jsx(TextContainer, {
            placeholder: placeholder,
            children: placeholder
          })]
        }) : /*#__PURE__*/_jsx(SingleValueContent, {
          children: selectedItems.length > 0 && selectedItems[0].length > 0 ? /*#__PURE__*/_jsx(TextContainer, {
            id: "selectValue",
            children: getLabelFromChildren(selectedItems[0])
          }) : /*#__PURE__*/_jsx(TextContainer, {
            placeholder: placeholder,
            children: placeholder
          })
        })
      }), clearable && selectedItems.length > 0 && /*#__PURE__*/_jsx(ClearableContainer, {
        size: size,
        disabled: disabled,
        tabIndex: -1,
        onClick: e => {
          e.stopPropagation();
          setSelectedItems([]);
        },
        children: /*#__PURE__*/_jsx(Close, {
          size: "sm",
          color: theme.colors.secondaryL3
        })
      }), /*#__PURE__*/_jsx(OpenButtonContainer, {
        size: size,
        disabled: disabled,
        tabIndex: -1,
        onClick: e => {
          e.stopPropagation();
          setOpen(!open);
        },
        children: open ? /*#__PURE__*/_jsx(ChevronUp, {
          size: size === 'lg' ? 'md' : 'sm',
          color: theme.colors.neutralL5
        }) : /*#__PURE__*/_jsx(ChevronDown, {
          size: size === 'lg' ? 'md' : 'sm',
          color: theme.colors.neutralL5
        })
      })]
    }), open && /*#__PURE__*/_jsx(SelectDropdownPositioner, {
      children: /*#__PURE__*/_jsx(SelectDropdown, {
        ref: dropdownRef,
        size: size,
        itemsHeight: dropdownHeight,
        children: selectItems.map((item, idx) => {
          return /*#__PURE__*/React.cloneElement(item, {
            key: item.props.value,
            selected: selectedItems.includes(item.props.value),
            hightlighted: selectItems.indexOf(item) === focusedIndex,
            size,
            description: item.props.description,
            onClick: e => handleSelectItemByValue(e.target.value)
          });
        })
      })
    }), helpGutter && /*#__PURE__*/_jsx(HelpMessage, {
      error: error,
      children: helpMessage
    })]
  });
};
export { Select };